import { computed, DestroyRef, Inject, Injectable, Signal, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, Observable, switchMap, tap } from 'rxjs';

import {
	AbstractAccountSettingsService,
	ACCOUNT_SETTINGS_SERVICE_INJECTOR,
	AbstractFilesService,
	AbstractProfileService,
	AlarisConfigService,
	extractCustomFields,
	FILES_SERVICE_INJECTOR,
	PROFILE_SERVICE_INJECTOR,
} from '@campaign-portal/components-library';
import { User } from '@campaign-portal/namespace/common/user';
import { DateTimeFormat } from '@campaign-portal/namespace/common/enums';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { CustomEntityField, CustomForm } from '@campaign-portal/namespace/entities/custom-forms/specs';
import {
	PersonalData,
	PersonalDataApi,
	PersonalDataField,
} from '@campaign-portal/contracts/account-settings/personal-data';
import { PartnersService } from '../services';
import { UserDetailsSubset } from '../types';
import { PublicImageUrl } from '../utils';

@Injectable({ providedIn: 'root' })
export class AlarisPersonalDataAdapter extends PersonalDataApi {
	private readonly raw = signal<User | null>(null);
	private readonly forms = signal<readonly CustomForm<exist>[]>([]);

	readonly data: Signal<PersonalData | null> = computed(() => {
		const u = this.raw();
		return u ? this.toPersonalData(u) : null;
	});

	private readonly partnersMap = toSignal(this.partners.map$, { initialValue: this.partners.map });

	readonly customFields: Signal<readonly CustomEntityField[]> = computed(() => {
		const id = this.raw()?.customFormId;
		if (!id) return [];
		return extractCustomFields(this.forms() as CustomForm<exist>[], id);
	});

	constructor(
		@Inject(ACCOUNT_SETTINGS_SERVICE_INJECTOR)
		private readonly account: AbstractAccountSettingsService,
		@Inject(FILES_SERVICE_INJECTOR)
		private readonly files: AbstractFilesService,
		@Inject(PROFILE_SERVICE_INJECTOR)
		private readonly profile: AbstractProfileService,
		private readonly config: AlarisConfigService,
		private readonly partners: PartnersService,
		destroyRef: DestroyRef
	) {
		super();
		this.account
			.load()
			.pipe(takeUntilDestroyed(destroyRef))
			.subscribe((user) => this.raw.set(user));

		this.account
			.readCustomForms()
			.pipe(takeUntilDestroyed(destroyRef))
			.subscribe((forms) => this.forms.set(forms));
	}

	update(field: PersonalDataField, value: string): Observable<PersonalData> {
		const current = this.requireCurrent();
		return this.commit(this.applyPatch(current, field, value));
	}

	updateName(firstName: string, lastName: string): Observable<PersonalData> {
		const current = this.requireCurrent();
		const patched: User = { ...current, details: { ...current.details, firstName, lastName } };
		return this.commit(patched);
	}

	updateCustomFields(values: Record<string, unknown>): Observable<PersonalData> {
		const current = this.requireCurrent();
		const patched: User = { ...current, customFields: { ...current.customFields, ...values } };
		return this.commit(patched);
	}

	uploadPhoto(blob: Blob): Observable<PersonalData> {
		const current = this.requireCurrent();
		const type = blob.type || 'image/png';
		const ext = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' }[type] ?? 'png';
		const file = new File([blob], `avatar.${ext}`, { type });

		return this.files.upload(file).pipe(
			map((r) => r.Data?.id ?? ''),
			filter((fileId): fileId is string => fileId !== ''),
			switchMap((fileId) => {
				const patched: User = { ...current, details: { ...current.details, photoUrl: fileId } };
				return this.commit(patched);
			})
		);
	}

	deletePhoto(): Observable<PersonalData> {
		const current = this.requireCurrent();
		const patched: User = { ...current, details: { ...current.details, photoUrl: null } };
		return this.commit(patched);
	}

	private toPersonalData(u: User): PersonalData {
		const details = (u.details ?? {}) as UserDetailsSubset;
		const firstName = details.firstName ?? '';
		const lastName = details.lastName ?? '';
		const displayName = `${firstName} ${lastName}`.trim() || u.login || u.email;
		return {
			accountId: String(u.id ?? ''),
			displayName,
			photoUrl: details.photoUrl ? new PublicImageUrl(this.config.api, details.photoUrl).value() : null,
			firstName,
			lastName,
			email: u.email ?? '',
			company: this.partnerName(u.partnerId),
			dateFormat: details.dateFormat ?? '',
			customFieldValues: u.customFields ?? {},
		};
	}

	private partnerName(partnerId?: Id<exist> | null): string {
		if (partnerId === undefined || partnerId === null) {
			return '';
		}
		return this.partnersMap().get(partnerId)?.name ?? '';
	}

	private requireCurrent(): User {
		const current = this.raw();
		if (!current) {
			throw new Error('PersonalData not loaded yet');
		}
		return current;
	}

	private commit(patched: User): Observable<PersonalData> {
		return this.account.update(patched).pipe(
			tap((resp) => {
				const updated = resp.Data?.at(0);
				if (resp.Success && updated) {
					this.profile.register(this.profile.convert(updated));
					this.raw.set(updated);
				}
			}),
			map(() => this.toPersonalData(this.raw() ?? patched))
		);
	}

	private applyPatch(user: User, field: PersonalDataField, value: string): User {
		switch (field) {
			case 'email':
				return { ...user, email: value };
			case 'company':
				return { ...user, details: { ...user.details, company: value } };
			case 'dateFormat':
				return { ...user, details: { ...user.details, dateFormat: value as DateTimeFormat } };
		}
	}
}
